import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ArcGISSelectionPersistenceService {
    private readonly STORAGE_PREFIX = 'arcgis_selection';

    constructor() {}

    private getStorageKey(customerId, arcgisUsername) {
        return `${this.STORAGE_PREFIX}_${customerId}_${arcgisUsername}`;
    }

    public saveSelection(customerId, arcgisUsername, selectedAssets, layerTreeState) {
        if (!customerId || !arcgisUsername) {
            console.warn('Cannot save selection: missing customerId or arcgisUsername');
            return;
        }

        const key = this.getStorageKey(customerId, arcgisUsername);
        const state = {
            customerId,
            arcgisUsername,
            timestamp: new Date().toISOString(),
            selectedAssets,
            layerTreeState
        };

        try {
            localStorage.setItem(key, JSON.stringify(state));
        } catch (error) {
            console.error('Failed to save ArcGIS selection to localStorage:', error);

            if (error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded - attempting cleanup');
                this.cleanupOldSelections();

                try {
                    localStorage.setItem(key, JSON.stringify(state));
                } catch (retryError) {
                    console.error('Save failed even after cleanup:', retryError);
                }
            }
        }
    }

    public loadSelection(customerId, arcgisUsername) {
        if (!customerId || !arcgisUsername) {
            console.warn('Cannot load selection: missing customerId or arcgisUsername');
            return null;
        }

        const key = this.getStorageKey(customerId, arcgisUsername);

        try {
            const stored = localStorage.getItem(key);
            if (!stored) {
                return null;
            }

            const state = JSON.parse(stored);

            if (!state.selectedAssets || !state.layerTreeState) {
                console.warn('Invalid selection state structure - ignoring');
                return null;
            }

            return state;
        } catch (error) {
            console.error('Failed to load ArcGIS selection from localStorage:', error);
            return null;
        }
    }

    public hasSelection(customerId, arcgisUsername) {
        const key = this.getStorageKey(customerId, arcgisUsername);
        return localStorage.getItem(key) !== null;
    }

    public clearSelection(customerId, arcgisUsername) {
        const key = this.getStorageKey(customerId, arcgisUsername);
        localStorage.removeItem(key);
    }

    public clearAllSelectionsForCustomer(customerId) {
        const prefix = `${this.STORAGE_PREFIX}_${customerId}_`;
        const keys = Object.keys(localStorage).filter(k => k.startsWith(prefix));

        keys.forEach(key => localStorage.removeItem(key));
    }

    public clearAllSelections() {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));
        keys.forEach(key => localStorage.removeItem(key));
    }

    public getAllStoredSelections() {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));

        return keys.map(key => {
            try {
                const state = JSON.parse(localStorage.getItem(key));
                return {
                    customerId: state.customerId,
                    arcgisUsername: state.arcgisUsername,
                    timestamp: state.timestamp,
                    assetCount: state.selectedAssets?.length || 0,
                    layerCount: state.layerTreeState?.reduce((sum, tree) => sum + tree.layers.length, 0) || 0
                };
            } catch (error) {
                console.error(`Invalid data for key ${key}:`, error);
                return null;
            }
        }).filter(item => item !== null);
    }

    private cleanupOldSelections() {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 90);

        const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));
        let removedCount = 0;

        keys.forEach(key => {
            try {
                const state = JSON.parse(localStorage.getItem(key));
                const savedDate = new Date(state.timestamp);

                if (savedDate < cutoffDate) {
                    localStorage.removeItem(key);
                    removedCount++;
                }
            } catch (error) {
                localStorage.removeItem(key);
                removedCount++;
            }
        });
    }

    public getStorageStats() {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(this.STORAGE_PREFIX));

        let totalSize = 0;
        keys.forEach(key => {
            const item = localStorage.getItem(key);
            totalSize += item ? item.length : 0;
        });

        return {
            selectionCount: keys.length,
            estimatedSizeKB: Math.round(totalSize / 1024 * 100) / 100,
            keys: keys
        };
    }
}
